import { api } from '@goaltree/backend/convex/_generated/api'
import type { Id } from '@goaltree/backend/convex/_generated/dataModel'
import { MUST_DO_LIMIT } from '@goaltree/backend/convex/lib/mustDo'
import { useMutation } from 'convex/react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { localDateStr } from '@/hooks/use-today-tasks'
import { patchNode } from '@/lib/nodes-optimistic'
import { useMustDoStore } from '@/store/must-do.store'

export type MustDoDay = 'today' | 'tomorrow'

// Commit a task to the "Must do" lane (or clear it with null). Resolves to
// whether it worked; a full lane explains itself in a toast.
export function useMustDo() {
  const { t } = useTranslation()
  const set = useMutation(api.nodes.setMustDo).withOptimisticUpdate(
    (localStore, { taskId, day, localDate }) => {
      if (day === null) return patchNode(localStore, taskId, { mustDoOn: null })
      if (day !== 'today') return
      // Only when today's lane visibly has room; a full lane is answered by the server.
      const today = localStore.getQuery(api.dailySurface.getTodayWithContext, { localDate })
      const lane = today?.tasks.filter(
        (t) => t.mustDo && t.completedAt === null && t.taskId !== taskId,
      )
      if (!lane || lane.length >= MUST_DO_LIMIT) return
      patchNode(localStore, taskId, (prev) => ({
        mustDoOn: localDate,
        mustDo: true,
        ...(prev.mustDo ? {} : { putOffDays: 0 }),
      }))
    },
  )
  return async (taskId: Id<'nodes'>, day: MustDoDay | null, quiet = false) => {
    const res = await set({ taskId, day, localDate: localDateStr() }).catch(() => null)
    if (!res) {
      toast.error(t('mustDo.error'))
      return false
    }
    if (!res.ok) {
      if (day) useMustDoStore.getState().setFull({ taskId, day, lane: res.lane })
      else toast.error(t('mustDo.full'), { description: t('mustDo.fullHint') })
      return false
    }
    if (!quiet)
      toast.success(
        day === null
          ? t('mustDo.removed')
          : day === 'today'
            ? t('mustDo.addedToday')
            : t('mustDo.addedTomorrow'),
      )
    return true
  }
}

const TODAY_WORDS =
  /(?<!\p{L})(today|tonight|hoy|esta noche|aujourd['’]hui|ce soir|hoje)(?!\p{L})|今日|今夜|今天|今晚/iu
const TOMORROW_WORDS = /(?<!\p{L})(tomorrow|mañana|demain|amanhã)(?!\p{L})|明日|明天/iu

// "…tomorrow" in a typed task → which lane it belongs in, if any.
export function detectMustDoDay(text: string): MustDoDay | null {
  if (TOMORROW_WORDS.test(text)) return 'tomorrow'
  if (TODAY_WORDS.test(text)) return 'today'
  return null
}
